/* iter564 — Onboarding Wizard (3 steps).
 *
 * Foolproof first-visit experience за нови потребители. Заменя стария
 * 7-стъпков DashboardGuide на auto-open. Старото ръководство остава
 * достъпно ръчно през „Ръководство" бутона (DashboardGuide).
 *
 * Steps:
 *   1. Привет — приветствие + кратко обяснение какво ще получи потребителят
 *   2. Разкажете ни за себе си — 3 quick inputs (цел / тегло / напомняния)
 *   3. Първата стъпка — избор от 3 действия за веднага
 *
 * Persistence: localStorage flag "nutramilo_onboarding_wizard_seen" = "1"
 * след завършване или skip. Профилните данни се запазват през PATCH /auth/me.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, ArrowRight, X, ChevronLeft, Target, Bell,
  Coffee, MessageSquare, ClipboardCheck, Camera, Dna,
} from "lucide-react";
import { useAuth } from "@/App";
import { toast } from "sonner";

const STORAGE_KEY = "nutramilo_onboarding_wizard_seen";

const GOALS = [
  { value: "weight_loss", label: "Отслабване" },
  { value: "maintenance", label: "Поддръжка" },
  { value: "muscle_gain", label: "Качване на мускул" },
  { value: "metabolic_health", label: "Метаболитно здраве" },
  { value: "perinatal", label: "Бременност / кърмене" },
];

const ACTIONS = [
  {
    id: "breakfast",
    icon: Coffee,
    title: "Записвам първото си ястие",
    subtitle: "Една закуска. Виждате как работи дневникът.",
    path: "/dashboard/diary",
    tint: "amber",
  },
  {
    id: "ai",
    icon: MessageSquare,
    title: "Питам Nutramilo с мои думи",
    subtitle: "Разговорно, без сложни думи. Ще Ви ориентираме.",
    path: "/dashboard",
    tint: "pink",
  },
  {
    id: "assessment",
    icon: ClipboardCheck,
    title: "Допълвам профила си",
    subtitle: "Кратка оценка, около три минути. Отключва личните показатели.",
    path: "/assessment",
    tint: "violet",
  },
];

const TINT_BG = {
  amber: "bg-amber-100 text-amber-700",
  pink: "bg-pink-100 text-pink-700",
  violet: "bg-violet-100 text-violet-700",
};

const firstNameOf = (user) =>
  user?.profile?.personal?.firstName ||
  (typeof user?.name === "string" ? user.name.split(" ")[0] : "") ||
  "";

export default function OnboardingWizard() {
  const { user, API, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [goal, setGoal] = useState("");
  const [weight, setWeight] = useState("");
  const [reminders, setReminders] = useState(true);

  // Auto-open on first visit if user is logged in and has not seen the wizard.
  useEffect(() => {
    if (!user) return;
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (seen) return;
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    } catch {
      // ignore quota issues
    }
  }, [user]);

  const markSeen = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore
    }
  };

  const close = () => {
    markSeen();
    setOpen(false);
  };

  const skip = () => {
    close();
    toast("Можеш да допълниш профила по-късно от Профил → Личн. данни.", {
      duration: 3000,
    });
  };

  const saveProfileBasics = async () => {
    if (!goal && !weight) {
      setStep(2);
      return;
    }
    setSaving(true);
    const token =
      localStorage.getItem("nutramilo_access_token") ||
      sessionStorage.getItem("nutramilo_access_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // iter564 — split payload across two endpoints:
    //  • PATCH /auth/me — profile fields (goal, weight)
    //  • PUT /preferences — reminders_enabled (lives in preferences schema)
    const profileBody = { profile: {} };
    if (goal) {
      profileBody.profile.personal = { goal };
    }
    if (weight) {
      const w = parseFloat(weight);
      if (!Number.isNaN(w) && w > 0) {
        profileBody.profile.body = { weight: w };
      }
    }

    try {
      const requests = [];
      if (Object.keys(profileBody.profile).length > 0) {
        requests.push(axios.patch(`${API}/auth/me`, profileBody, { headers }));
      }
      requests.push(
        axios.put(`${API}/preferences`, { reminders_enabled: reminders }, { headers })
      );
      await Promise.all(requests);
      if (refreshUser) {
        try { await refreshUser(); } catch { /* ignore */ }
      }
      toast.success("Запазихме информацията. Благодарим!", { duration: 2500 });
    } catch {
      toast.error("Нещо се обърка при запис. Опитай по-късно от Профил.", {
        duration: 3500,
      });
    } finally {
      setSaving(false);
      setStep(2);
    }
  };

  const chooseAction = (a) => {
    markSeen();
    setOpen(false);
    setTimeout(() => navigate(a.path), 150);
  };

  if (!user || !open) return null;

  const name = firstNameOf(user);
  const nameSuffix = name ? `, ${name}` : "";

  return (
    <div
      data-testid="onboarding-wizard"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-stone-900/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        // Click outside closes only via X button — prevent accidental dismiss
        if (e.target === e.currentTarget) {
          // do nothing — require explicit X
        }
      }}
    >
      <div
        /* iter972 — Mobile fix: switch to dynamic viewport height (`dvh`)
           so the browser chrome bars don't clip the scroll area. On very
           small phones the modal now takes the FULL screen (no top/side
           padding); on ≥sm we keep the centered rounded card. */
        className="relative w-full h-[100dvh] sm:h-auto sm:max-h-[88vh] max-w-xl flex flex-col bg-white sm:rounded-3xl shadow-2xl border border-stone-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Soft brand glow */}
        <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full bg-pink-200/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-violet-200/30 blur-3xl" />

        {/* Header bar with progress dots + close */}
        <div className="relative flex items-center justify-between px-6 pt-5">
          <div className="flex items-center gap-1.5">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step ? "w-8 bg-stone-900" : "w-3 bg-stone-300"
                }`}
                aria-hidden
              />
            ))}
          </div>
          <button
            type="button"
            onClick={skip}
            data-testid="onboarding-close"
            aria-label="Затвори"
            className="text-stone-400 hover:text-stone-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* iter595 — съдържанието скролира на малки екрани; X и бутоните са достъпни */}
        <div className="relative px-6 py-6 sm:px-8 sm:py-8 flex-1 min-h-0 overflow-y-auto">
          {/* Step 1 — Привет */}
          {step === 0 && (
            <div data-testid="onboarding-step-1" className="space-y-5">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-pink-700">
                <Sparkles className="w-3.5 h-3.5" />
                Стъпка 1 от 3
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 leading-tight">
                Здравейте{nameSuffix}. Радваме се, че сте тук.
              </h2>
              <p className="text-base text-stone-700 leading-relaxed">
                Nutramilo Ви помага да разберете как храната, сънят и движението
                влияят на тялото Ви — на ясен и разбираем език.
              </p>
              <div className="space-y-3 pt-1">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                    <Camera className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="flex-1 pt-0.5">
                    <div className="text-sm font-bold text-stone-900">
                      Лесно записване
                    </div>
                    <p className="mt-0.5 text-sm text-stone-600 leading-relaxed">
                      Снимайте храната си или я опишете с няколко думи. Останалото
                      правим ние.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                    <Dna className="w-5 h-5 text-violet-700" />
                  </div>
                  <div className="flex-1 pt-0.5">
                    <div className="text-sm font-bold text-stone-900">
                      Личен подход
                    </div>
                    <p className="mt-0.5 text-sm text-stone-600 leading-relaxed">
                      Всички показатели се изчисляват според Вашите цели,
                      навици и обмяна.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-pink-100 flex items-center justify-center shrink-0">
                    <Sparkles className="w-5 h-5 text-pink-700" />
                  </div>
                  <div className="flex-1 pt-0.5">
                    <div className="text-sm font-bold text-stone-900">
                      Попитайте по всяко време
                    </div>
                    <p className="mt-0.5 text-sm text-stone-600 leading-relaxed">
                      Получавате ясни отговори от помощник с изкуствен интелект,
                      стъпил на медицинска и научна основа.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={skip}
                  data-testid="onboarding-skip"
                  className="px-4 py-2.5 rounded-full text-stone-600 hover:text-stone-900 font-semibold text-sm transition-colors"
                >
                  Не сега
                </button>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  data-testid="onboarding-step-1-next"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-stone-900 text-white font-semibold text-sm hover:bg-stone-800 transition-colors shadow-sm"
                >
                  Продължавам
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2 — За теб */}
          {step === 1 && (
            <div data-testid="onboarding-step-2" className="space-y-5">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-pink-700">
                <Target className="w-3.5 h-3.5" />
                Стъпка 2 от 3
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 leading-tight">
                Разкажете ни за себе си
              </h2>
              <p className="text-base text-stone-700 leading-relaxed">
                Само две неща за начало. Останалото можете да допълните по-късно.
              </p>

              {/* Goal */}
              <div className="space-y-1.5">
                <label
                  htmlFor="onboarding-goal"
                  className="text-sm font-semibold text-stone-900"
                >
                  Каква е целта ти?
                </label>
                <select
                  id="onboarding-goal"
                  data-testid="onboarding-goal-select"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-300 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300"
                >
                  <option value="">Изберете…</option>
                  {GOALS.map((g) => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>

              {/* Weight (optional) */}
              <div className="space-y-1.5">
                <label
                  htmlFor="onboarding-weight"
                  className="text-sm font-semibold text-stone-900"
                >
                  Колко тежиш? <span className="text-stone-400 font-normal">(незадължително)</span>
                </label>
                <div className="relative">
                  <input
                    id="onboarding-weight"
                    data-testid="onboarding-weight-input"
                    type="number"
                    inputMode="decimal"
                    min="20"
                    max="300"
                    step="0.1"
                    placeholder="напр. 72"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="w-full px-4 py-2.5 pr-12 rounded-xl border border-stone-300 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-300"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-stone-400">kg</span>
                </div>
              </div>

              {/* Reminders */}
              <label
                htmlFor="onboarding-reminders"
                className="flex items-start gap-3 p-3 rounded-xl bg-stone-50 border border-stone-200 cursor-pointer hover:bg-stone-100 transition-colors"
              >
                <input
                  id="onboarding-reminders"
                  data-testid="onboarding-reminders-checkbox"
                  type="checkbox"
                  checked={reminders}
                  onChange={(e) => setReminders(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded text-pink-600 focus:ring-pink-300 border-stone-300"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-stone-900">
                    <Bell className="w-3.5 h-3.5 text-stone-500" />
                    Тихи напомняния
                  </div>
                  <div className="text-xs text-stone-600 mt-0.5 leading-relaxed">
                    Малки нежни напомняния (Вода / Закуска / Сън). Никакво
                    звънене. Можеш да ги изключиш по всяко време.
                  </div>
                </div>
              </label>

              <div className="flex items-center justify-between pt-4">
                <button
                  type="button"
                  onClick={() => setStep(0)}
                  data-testid="onboarding-step-2-back"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-stone-600 hover:text-stone-900 font-semibold text-sm transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Назад
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={skip}
                    data-testid="onboarding-step-2-skip"
                    className="px-4 py-2.5 rounded-full text-stone-600 hover:text-stone-900 font-semibold text-sm transition-colors"
                  >
                    Не сега
                  </button>
                  <button
                    type="button"
                    onClick={saveProfileBasics}
                    disabled={saving}
                    data-testid="onboarding-step-2-next"
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-stone-900 text-white font-semibold text-sm hover:bg-stone-800 disabled:opacity-60 disabled:cursor-wait transition-colors shadow-sm"
                  >
                    {saving ? "Запазвам…" : "Готово"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — Първата стъпка */}
          {step === 2 && (
            <div data-testid="onboarding-step-3" className="space-y-5">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-pink-700">
                <Sparkles className="w-3.5 h-3.5" />
                Стъпка 3 от 3
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 leading-tight">
                Откъде да започнем?
              </h2>
              <p className="text-base text-stone-700 leading-relaxed">
                Едно действие сега е по-ценно от десет утре. Изберете каквото Ви
                привлече — винаги можете да опитате и другите.
              </p>
              <div className="space-y-2.5 pt-2">
                {ACTIONS.map((a) => {
                  const Icon = a.icon;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => chooseAction(a)}
                      data-testid={`onboarding-action-${a.id}`}
                      className="w-full text-left p-4 rounded-2xl bg-white border border-stone-200 hover:border-stone-400 hover:shadow-md transition-all duration-200 group"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${TINT_BG[a.tint]}`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-base font-bold text-stone-900 leading-tight">
                            {a.title}
                          </div>
                          <p className="mt-0.5 text-sm text-stone-600 leading-relaxed">
                            {a.subtitle}
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-stone-900 group-hover:translate-x-0.5 transition-all mt-1" />
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between pt-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  data-testid="onboarding-step-3-back"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-stone-600 hover:text-stone-900 font-semibold text-sm transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Назад
                </button>
                <button
                  type="button"
                  onClick={close}
                  data-testid="onboarding-step-3-finish"
                  className="px-4 py-2.5 rounded-full text-stone-600 hover:text-stone-900 font-semibold text-sm transition-colors"
                >
                  Ще разгледам сам
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
